// Covenant Calibration (capture-only) — card 1 of 3 on the loan Profile
// tab. A table of labelled threshold rows, each pre-filled with a starting
// money value the server returns; staff edit + confirm. NO calculations, no
// derived values — the rows render whatever amounts[] the GET returns, in
// order.

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  SkeletonList,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  useConfirmCalibration,
  useCovenantCalibration,
  useUpdateCalibrationAmounts,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { CovenantConfirmRow } from "./CovenantConfirmRow";

function parseMoney(raw: string): number {
  return Number(raw.replace(/[$,]/g, ""));
}

export function CovenantCalibrationCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canEdit = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const toast = useToast();
  const query = useCovenantCalibration(loanId);
  const update = useUpdateCalibrationAmounts(loanId);
  const confirm = useConfirmCalibration(loanId);

  const data = query.data;
  const amounts = useMemo(() => data?.amounts ?? [], [data]);

  const [editing, setEditing] = useState(false);
  // code → raw string input while editing.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Re-hydrate the draft whenever an edit starts or fresh data arrives.
  useEffect(() => {
    if (!editing) return;
    setDraft(Object.fromEntries(amounts.map((a) => [a.code, String(a.amount)])));
  }, [editing, amounts]);

  const startEdit = () => {
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setError(null);
    const rows = amounts.map((a) => ({
      code: a.code,
      amount: parseMoney(draft[a.code] ?? String(a.amount)),
    }));
    const bad = rows.find((r) => !Number.isFinite(r.amount));
    if (bad) {
      setError("Every amount must be a number.");
      return;
    }
    try {
      await update.mutateAsync({ amounts: rows });
      toast.success("Calibration amounts saved");
      setEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const onConfirm = async () => {
    try {
      await confirm.mutateAsync();
      toast.success("Calibration confirmed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="covenant-calibration-card">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Covenant Calibration
          </div>
          <p className="text-caption text-gray-mid m-0">
            Starting covenant thresholds — capture only.
          </p>
        </div>
        {canEdit && !editing && !query.isLoading && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={startEdit}
            data-testid="covenant-calibration-edit"
          >
            Edit
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <SkeletonList rows={4} data-testid="covenant-calibration-loading" />
      ) : query.isError ? (
        <p className="text-small text-status-error m-0" role="alert">
          Couldn&apos;t load covenant calibration. Please retry.
        </p>
      ) : amounts.length === 0 ? (
        <p className="text-small text-gray-mid m-0">No calibration rows yet.</p>
      ) : (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Label</TH>
                <TH>Section</TH>
                <TH className="text-right">Amount</TH>
              </TR>
            </THead>
            <TBody>
              {amounts.map((row) => (
                <TR key={row.code} data-testid={`covenant-amount-row-${row.code}`}>
                  <TD>{row.label}</TD>
                  <TD className="text-gray-mid">{row.section}</TD>
                  <TD className="text-right">
                    {editing ? (
                      <Input
                        inputMode="decimal"
                        className="h-8 text-right"
                        aria-label={`${row.label} amount`}
                        value={draft[row.code] ?? String(row.amount)}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, [row.code]: e.target.value }))
                        }
                        data-testid={`covenant-amount-input-${row.code}`}
                      />
                    ) : (
                      <span className="font-mono tabular-nums">
                        {formatCurrency(row.amount)}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {error && (
            <p
              className="text-caption text-status-error mt-space-3 m-0"
              role="alert"
              data-testid="covenant-calibration-error"
            >
              {error}
            </p>
          )}

          {editing && (
            <div className="mt-space-3 flex justify-end gap-space-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                loading={update.isPending}
                data-testid="covenant-calibration-save"
              >
                Save
              </Button>
            </div>
          )}

          {data && (
            <CovenantConfirmRow
              confirmation={data.calibrationConfirmation}
              canEdit={canEdit}
              editing={editing}
              onConfirm={() => void onConfirm()}
              confirming={confirm.isPending}
              testIdPrefix="covenant-calibration"
            />
          )}
        </>
      )}
    </Card>
  );
}
